import {
  DSTable,
  DSTableBody,
  DSTableCell,
  DSTableContainer,
  DSTableHead,
  DSTableRow,
} from "views/system-design/Table";
import DSBox from "views/system-design/DSBox";
import DSChip from "views/system-design/DSChip";
import DSTypography from "views/system-design/DSTypography";
import DSTooltip from "views/system-design/DSTooltip";
import { alpha, useTheme } from "@mui/material/styles";
import { Author } from "api/author";
import { useIsMobile } from "../../hooks/useIsMobile";
import { ReviewRequest } from "../types";
import { formatWaiting, hasNewCommits, userName, waitingColor } from "../utils";
import {
  PolicyText,
  PriorityChip,
  PullChips,
  ReviewStatusChip,
  ReviewerAvatars,
} from "./ReviewChips";

interface ReviewsTableProps {
  requests: ReviewRequest[];
  users: Author[];
  selectedId?: string;
  onSelect: (request: ReviewRequest) => void;
  emptyText: string;
}

export default function ReviewsTable({
  requests,
  users,
  selectedId,
  onSelect,
  emptyText,
}: ReviewsTableProps) {
  const theme = useTheme();
  const isMobile = useIsMobile();

  if (requests.length === 0) {
    return (
      <DSBox sx={{ p: 4, textAlign: "center" }}>
        <DSTypography sx={{ color: "text.secondary" }}>
          {emptyText}
        </DSTypography>
      </DSBox>
    );
  }

  return (
    <DSTableContainer sx={{ flex: 1, overflow: "auto" }}>
      <DSTable stickyHeader size="small">
        <DSTableHead>
          <DSTableRow>
            <DSTableCell>Request</DSTableCell>
            <DSTableCell>PRs</DSTableCell>
            {!isMobile && <DSTableCell>Requester</DSTableCell>}
            <DSTableCell>Reviewers</DSTableCell>
            {!isMobile && <DSTableCell>Approvals</DSTableCell>}
            <DSTableCell>Status</DSTableCell>
            <DSTableCell>Waiting</DSTableCell>
            {!isMobile && <DSTableCell>Round</DSTableCell>}
          </DSTableRow>
        </DSTableHead>
        <DSTableBody>
          {requests.map((request) => {
            const selected = request.id === selectedId;
            // Waiting time restarts with every round, so it always describes
            // how long the current ask has been sitting.
            const waitingSince =
              request.status === "open"
                ? request.roundStartedAt
                : request.updatedAt;
            return (
              <DSTableRow
                key={request.id}
                hover
                onClick={() => onSelect(request)}
                sx={{
                  cursor: "pointer",
                  bgcolor: selected
                    ? alpha(theme.palette.primary.main, 0.08)
                    : undefined,
                }}
              >
                <DSTableCell sx={{ maxWidth: 360 }}>
                  <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {request.ticketKey && (
                      <DSTypography
                        variant="caption"
                        sx={{ fontWeight: 700, color: "text.secondary" }}
                      >
                        {request.ticketKey}
                      </DSTypography>
                    )}
                    <DSTypography
                      variant="body2"
                      noWrap
                      sx={{ fontWeight: 500 }}
                    >
                      {request.title}
                    </DSTypography>
                    <PriorityChip request={request} />
                    {hasNewCommits(request) && (
                      <DSTooltip title="New commits were pushed since this round started. Re-request when the fixes are ready.">
                        <DSChip
                          label="new commits"
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: 10, borderColor: "warning.main" }}
                        />
                      </DSTooltip>
                    )}
                  </DSBox>
                </DSTableCell>
                <DSTableCell>
                  <PullChips pulls={request.prs} />
                </DSTableCell>
                {!isMobile && (
                  <DSTableCell>
                    {userName(request.requesterId, users)}
                  </DSTableCell>
                )}
                <DSTableCell>
                  <ReviewerAvatars request={request} users={users} />
                </DSTableCell>
                {!isMobile && (
                  <DSTableCell>
                    <PolicyText request={request} />
                  </DSTableCell>
                )}
                <DSTableCell>
                  <ReviewStatusChip request={request} />
                </DSTableCell>
                <DSTableCell>
                  {request.status === "open" ? (
                    <DSTypography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: waitingColor(waitingSince),
                      }}
                    >
                      {formatWaiting(waitingSince)}
                    </DSTypography>
                  ) : (
                    <DSTypography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      —
                    </DSTypography>
                  )}
                </DSTableCell>
                {!isMobile && (
                  <DSTableCell>
                    <DSTypography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {request.round}
                    </DSTypography>
                  </DSTableCell>
                )}
              </DSTableRow>
            );
          })}
        </DSTableBody>
      </DSTable>
    </DSTableContainer>
  );
}
